/**
 * SponsorCheckout.tsx
 * Route: /sponsor  (or /pay-it-forward)
 *
 * Handles: Individual sponsors, corporates, NGOs, churches
 * Features: Multi-seat, billing interval toggle, social/logo opt-in,
 *           pay-it-forward pool vs named-seat sponsorship, dedication message
 */
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Heart, Building2, Church, Globe, Users, Check,
  ChevronRight, ChevronDown, ChevronUp, Instagram,
  Linkedin, Facebook, Twitter, Star, Shield, Sparkles,
  ArrowRight, Info
} from 'lucide-react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useSponsorPricing } from '@/hooks/useSponsorPricing'

// Handled via supabase client

type PlanTier = 'standard' | 'elite' | 'bundle'
type SponsorType = 'individual' | 'business' | 'ngo' | 'church'
type Interval = 'month' | 'six_months' | 'year'
type SponsorMode = 'named' | 'pool'   // named = specific children; pool = pay-it-forward
type SocialPlatform = 'instagram' | 'facebook' | 'twitter' | 'linkedin' | 'tiktok' | 'youtube'

const SOCIAL_ICONS: Record<SocialPlatform, React.ReactNode> = {
  instagram: <Instagram size={14} />,
  facebook: <Facebook size={14} />,
  twitter: <Twitter size={14} />,
  linkedin: <Linkedin size={14} />,
  tiktok: <span className="text-xs font-black">Tk</span>,
  youtube: <span className="text-xs font-black">Yt</span>,
}

export default function SponsorCheckout() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()
  const supabase = createClientComponentClient()

  // --- Theme ---
  const G = '#d4a843' // Gold
  const M = '#06060c' // Main BG (Deep Black)
  const CA = '#111118' // Card
  const BR = 'rgba(212,168,67,0.1)' // Border
  const TX = '#ffffff'
  const MU = '#a1a1aa'
  const SE = '"Outfit", serif'

  const INTERVAL_LABELS: Record<Interval, string> = {
    month: t('sponsor.corporate.intervals.month', 'Monthly'),
    six_months: t('sponsor.corporate.intervals.six_months', '6-Monthly'),
    year: t('sponsor.corporate.intervals.year', 'Annual'),
  }

  const INTERVAL_SAVINGS: Record<Interval, string> = {
    month: '',
    six_months: t('sponsor.corporate.intervals.save6', 'Save 9%'),
    year: t('sponsor.corporate.intervals.save12', 'Save 12%'),
  }

  const SPONSOR_TYPES: { value: SponsorType, label: string, icon: React.ReactNode, desc: string }[] = [
    { value: 'individual', label: t('sponsor.corporate.types.individual.label', 'Individual'), icon: <Heart size={20} />, desc: t('sponsor.corporate.types.individual.desc', 'Sponsor one or more children personally') },
    { value: 'business', label: t('sponsor.corporate.types.business.label', 'Business'), icon: <Building2 size={20} />, desc: t('sponsor.corporate.types.business.desc', 'Corporate CSR or team giving programme') },
    { value: 'ngo', label: t('sponsor.corporate.types.ngo.label', 'NGO / Charity'), icon: <Globe size={20} />, desc: t('sponsor.corporate.types.ngo.desc', 'Place children from your community') },
    { value: 'church', label: t('sponsor.corporate.types.church.label', 'Church'), icon: <Church size={20} />, desc: t('sponsor.corporate.types.church.desc', 'Sponsor congregation families in need') },
  ]

  const [sponsorType, setSponsorType] = useState<SponsorType>('individual')
  const [interval, setInterval] = useState<Interval>('month')
  const [seats, setSeats] = useState(1)
  const [mode, setMode] = useState<SponsorMode>(() => new URLSearchParams(window.location.search).get('mode') === 'named' ? 'named' : 'pool')
  const [orgName, setOrgName] = useState('')
  const [orgLogo, setOrgLogo] = useState('')
  const [socialHandle, setSocialHandle] = useState('')
  const [socialPlatform, setSocialPlatform] = useState<SocialPlatform>('instagram')
  const [allowTag, setAllowTag] = useState(false)
  const [allowLogo, setAllowLogo] = useState(false)
  const [dedication, setDedication] = useState('')
  const [anonymous, setAnonymous] = useState(false)
  const [updatePref, setUpdatePref] = useState<'none' | 'progress' | 'full'>(() => new URLSearchParams(window.location.search).get('mode') === 'named' ? 'full' : 'progress')
  const [updateTouched, setUpdateTouched] = useState(false)
    const [showAdvanced, setShowAdvanced] = useState(false)
  const [loading, setLoading] = useState(false)

    const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Existing sponsors get a manage link (mirrors SponsorHub's hasExisting)
  const [hasExisting, setHasExisting] = useState(false)
  useEffect(() => {
    if (!user) return
    supabase.from('facility_seat_sponsorships')
      .select('id', { count: 'exact', head: true })
      .eq('sponsor_id', user.id)
      .eq('status', 'active')
      .then(({ count }) => { if ((count ?? 0) > 0) setHasExisting(true) })
  }, [user])

  // Mode-reactive update defaults: pool → anonymised dashboard, named → full.
  // Only auto-applies until the sponsor makes an explicit choice.
  useEffect(() => {
    if (!updateTouched) setUpdatePref(mode === 'named' ? 'full' : 'progress')
  }, [mode, updateTouched])

  const [tier, setTier] = useState<PlanTier>('standard')
  const pricing = useSponsorPricing()

  const pricePerSeat = pricing[tier][interval]
  const total = pricePerSeat * seats

  async function handleCheckout() {
    if (!user) { navigate('/auth/login?next=/sponsor/corporate'); return }
    setLoading(true)

    let checkoutPlan = 'sponsor_seat_monthly'
    if (tier === 'standard') {
      if (mode === 'pool') checkoutPlan = 'pay_it_forward'
      else checkoutPlan = sponsorType === 'individual' ? 'sponsor_seat_monthly' : 'corporate_edu_pack'
    } else if (tier === 'elite') {
      checkoutPlan = sponsorType === 'individual' ? 'sponsor_elite_seat' : 'corporate_elite_pack'
    } else if (tier === 'bundle') {
      checkoutPlan = sponsorType === 'individual' ? 'sponsor_bundle_seat' : 'corporate_bundle_pack'
    }

    try {
      const { data, error } = await supabase.functions.invoke('billing', {
        body: {
          action: 'checkout',
          plan: checkoutPlan,
          interval,
          quantity: seats,
          destination: mode === 'pool' ? 'pay_it_forward' : 'sponsorship',
          metadata: {
            is_pay_it_forward: mode === 'pool',
            sponsor_type: sponsorType,
            organisation_name: orgName || undefined,
            organisation_logo_url: orgLogo || undefined,
            social_handle: allowTag ? socialHandle : undefined,
            social_platform: allowTag ? socialPlatform : undefined,
            allow_social_tag: allowTag,
            allow_logo_use: allowLogo,
            dedication_message: dedication || undefined,
            anonymous,
            update_preference: updatePref,
          }
        },
      })

      if (error || !data?.url) throw new Error(error?.message || data?.error || 'Failed to create checkout session')
      window.location.href = data.url
    } catch (e: any) {
      alert(e.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  const showOrgFields = sponsorType !== 'individual'

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: 'sans-serif' }}>
      {/* Header */}
            {/* Escape bar — never trap a visitor in checkout */}
      <div style={{ background: 'rgba(255,255,255,0.03)', borderBottom: `1px solid ${BR}`, padding: '12px 16px' }}>
        <div style={{ maxWidth: 650, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => navigate('/sponsor/give')}
            style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontWeight: 700, fontSize: 12, padding: 0 }}>
            ← Sponsor options
          </button>
          <button onClick={() => navigate('/sponsor#sample')}
            style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontWeight: 700, fontSize: 12, padding: 0 }}>
            See a sample child's progress
          </button>
          <button onClick={() => navigate('/sponsor/catalog')}
            style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontWeight: 700, fontSize: 12, padding: 0 }}>
            Browse the curriculum
          </button>
          <button onClick={() => navigate('/how-it-works')}
            style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontWeight: 700, fontSize: 12, padding: 0 }}>
            How sponsorship works
          </button>
          {hasExisting && (
            <button onClick={() => navigate('/sponsor/dashboard')}
              style={{ background: 'none', border: 'none', color: G, cursor: 'pointer', fontWeight: 800, fontSize: 12, padding: 0 }}>
              Manage my sponsorships →
            </button>
          )}
        </div>
      </div>
      <div style={{ background: `linear-gradient(135deg, ${M} 0%, #0c0c14 100%)`, borderBottom: `1px solid ${BR}`, padding: isMobile ? '40px 16px' : '60px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.1, pointerEvents: 'none', background: `radial-gradient(circle at 50% 50%, ${G} 0%, transparent 70%)` }} />
        <div style={{ position: 'relative', zIndex: 10, maxWidth: 800, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'inline-flex', alignSelf: 'center', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 999, background: 'rgba(212,168,67,0.1)', color: G, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', border: `1px solid ${BR}` }}>
            <Heart size={12} className="fill-current" /> {t('sponsor.corporate.eyebrow', 'Give the gift of education')}
          </div>
          <h1 style={{ fontFamily: SE, fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: G }}>
            {t('sponsor.corporate.title', "Sponsor a Child's Education")}
          </h1>
          <p style={{ color: MU, fontSize: 18, maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>
            {t('sponsor.corporate.subtitle', 'Give a child full access to the Standard Curriculum, Elite Academy, or both. You pay 50%, we match the rest.')}
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 650, margin: '0 auto', padding: isMobile ? '32px 16px 150px' : '48px 24px', display: 'flex', flexDirection: 'column', gap: isMobile ? 24 : 32 }}>

        {/* Sponsor type */}
        <div style={{ background: CA, borderRadius: 24, padding: 32, border: `1px solid ${BR}`, boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
          <h2 style={{ fontFamily: SE, color: G, margin: '0 0 20px', fontSize: 20 }}>{t('sponsor.corporate.typeTitle', 'I am sponsoring as a…')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            {SPONSOR_TYPES.map(st => (
              <button
                key={st.value}
                onClick={() => setSponsorType(st.value)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12, padding: 20, borderRadius: 16, border: `2.5px solid ${sponsorType === st.value ? G : BR}`,
                  textAlign: 'left', transition: 'all 0.2s', background: sponsorType === st.value ? 'rgba(212,168,67,0.05)' : 'transparent',
                  cursor: 'pointer'
                }}
              >
                <span style={{ marginTop: 2, color: sponsorType === st.value ? G : MU }}>
                  {st.icon}
                </span>
                <div>
                  <p style={{ fontWeight: 800, fontSize: 13, margin: '0 0 2px', color: sponsorType === st.value ? G : TX }}>{st.label}</p>
                  <p style={{ fontSize: 11, color: MU, margin: 0, lineHeight: 1.4 }}>{st.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Sponsorship mode */}
        <div style={{ background: CA, borderRadius: 24, padding: 32, border: `1px solid ${BR}`, boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
          <h2 style={{ fontFamily: SE, color: G, margin: '0 0 8px', fontSize: 20 }}>{t('sponsor.corporate.modeTitle', 'How would you like to give?')}</h2>
          <p style={{ fontSize: 13, color: MU, margin: '0 0 24px', lineHeight: 1.6 }}>
            {t('sponsor.corporate.modeSubtitle', 'Both options provide identical access; the difference is whether we assign a specific child to you or add to the general pool.')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {([
              { val: 'pool', label: t('sponsor.corporate.modes.pool.label', 'Pay It Forward Pool'), desc: t('sponsor.corporate.modes.pool.desc', 'Your seats go into a shared pool. Admin assigns them to hardship applicants who need help most. You receive progress updates without a named connection.') },
              { val: 'named', label: t('sponsor.corporate.modes.named.label', 'Named Seat Sponsorship'), desc: t('sponsor.corporate.modes.named.desc', 'You sponsor specific named children. You can view their progress, and they can see a message from you in their dashboard.') },
            ] as { val: SponsorMode; label: string; desc: string }[]).map(opt => (
              <button
                key={opt.val}
                onClick={() => setMode(opt.val)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'flex-start', gap: 16, padding: 24, borderRadius: 20, border: `2.5px solid ${mode === opt.val ? G : BR}`,
                  textAlign: 'left', transition: 'all 0.2s', background: mode === opt.val ? 'rgba(212,168,67,0.05)' : 'transparent', cursor: 'pointer'
                }}
              >
                <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${mode === opt.val ? G : MU}`, flexShrink: 0, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {mode === opt.val && <div style={{ width: 10, height: 10, borderRadius: '50%', background: G }} />}
                </div>
                <div>
                  <p style={{ fontWeight: 800, margin: '0 0 4px', color: mode === opt.val ? G : TX }}>{opt.label}</p>
                  <p style={{ fontSize: 12, color: MU, margin: 0, lineHeight: 1.5 }}>{opt.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Billing interval + seats */}
        <div style={{ background: CA, borderRadius: 24, padding: 32, border: `1px solid ${BR}`, boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
          <h2 style={{ fontFamily: SE, color: G, margin: '0 0 20px', fontSize: 20 }}>{t('sponsor.corporate.planTitle', 'Choose your plan')}</h2>

          {/* Tier toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 24, padding: 6, background: 'rgba(212,168,67,0.05)', borderRadius: 16 }}>
            {(['standard', 'elite', 'bundle'] as PlanTier[]).map(tierKey => (
              <button
                key={tierKey}
                onClick={() => setTier(tierKey)}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 12, fontSize: 13, fontWeight: 800, transition: 'all 0.2s', textTransform: 'capitalize',
                  background: tier === tierKey ? G : 'transparent', color: tier === tierKey ? M : MU, border: 'none', cursor: 'pointer'
                }}
              >
                {t(`sponsor.tabs.${tierKey}`, tierKey)}
              </button>
            ))}
          </div>

          <p style={{ fontSize: 11.5, color: MU, margin: '0 0 14px', lineHeight: 1.5 }}>
            Every sponsored child receives the full academy: Standard, Elite Academy, and Bible Journey, regardless of tier. Your tier sets your contribution per seat.
          </p>

          {/* Interval toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 32, padding: 6, background: 'rgba(212,168,67,0.05)', borderRadius: 16 }}>
            {(['month', 'six_months', 'year'] as Interval[]).map(iv => (
              <button
                key={iv}
                onClick={() => setInterval(iv)}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 12, fontSize: 13, fontWeight: 800, transition: 'all 0.2s', position: 'relative',
                  background: interval === iv ? G : 'transparent', color: interval === iv ? M : MU, border: 'none', cursor: 'pointer'
                }}
              >
                {INTERVAL_LABELS[iv]}
                {INTERVAL_SAVINGS[iv] && (
                  <span style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', padding: '2px 8px', background: G, color: M, fontSize: 9, fontWeight: 800, borderRadius: 999, textTransform: 'uppercase', border: `1px solid ${M}` }}>
                    {INTERVAL_SAVINGS[iv]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Seat picker */}
          <div style={{ marginBottom: 32 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
              {t('sponsor.corporate.seatsLabel', 'Number of Seats to Sponsor')}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <button
                onClick={() => setSeats(Math.max(1, seats - 1))}
                style={{ width: 48, height: 48, borderRadius: '50%', border: `1.5px solid ${BR}`, background: 'transparent', color: G, fontSize: 24, fontWeight: 300, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
              >−</button>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <span style={{ fontSize: isMobile ? 40 : 56, fontWeight: 800, color: G, fontFamily: SE }}>{seats}</span>
                <span style={{ color: MU, fontWeight: 700, marginLeft: 12, fontSize: 18 }}>
                  {seats === 1 ? t('sponsor.corporate.seat', 'seat') : t('sponsor.corporate.seats', 'seats')}
                </span>
              </div>
              <button
                onClick={() => setSeats(Math.min(200, seats + 1))}
                style={{ width: 48, height: 48, borderRadius: '50%', border: `1.5px solid ${BR}`, background: 'transparent', color: G, fontSize: 24, fontWeight: 300, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
              >+</button>
            </div>

            {/* Quick presets */}
            <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
              {[1, 3, 5, 10, 25, 50].map((n) => (
                <button
                  key={n}
                  onClick={() => setSeats(n)}
                  style={{
                    flex: 1, paddingTop: '8px', paddingBottom: '8px', borderRadius: 12, fontSize: 12, fontWeight: 800, transition: 'all 0.2s', border: `1px solid ${seats === n ? G : BR}`,
                    background: seats === n ? 'rgba(212,168,67,0.1)' : 'transparent', color: seats === n ? G : MU, cursor: 'pointer', padding: '10px 0'
                  }}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Price summary */}
          <div style={{ background: 'rgba(212,168,67,0.05)', border: `1px solid ${BR}`, borderRadius: 20, padding: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 800, color: G, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 4px' }}>{t('sponsor.corporate.totalLabel', 'Total')}</p>
              <p style={{ fontSize: 32, fontWeight: 800, color: TX, margin: 0, fontFamily: SE }}>
                ${total}
                <span style={{ fontSize: 16, color: MU, fontWeight: 700, marginLeft: 8 }}>
                  {interval === 'month' ? '/mo' : interval === 'six_months' ? '/6mo' : '/yr'}
                </span>
              </p>
              <p style={{ fontSize: 12, color: MU, margin: '4px 0 0', fontWeight: 500 }}>
                ${pricePerSeat} × {seats} {seats === 1 ? t('sponsor.corporate.seat', 'child') : t('sponsor.corporate.seats', 'children')}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 12, color: G, fontWeight: 800, margin: 0 }}>= ${(total / seats / 30).toFixed(2)}/{t('sponsor.corporate.perDayLabel', 'day per child')}</p>
              <p style={{ fontSize: 11, color: MU, margin: '2px 0 0', fontWeight: 500 }}>{t('sponsor.corporate.fullAccessNote', 'Full curriculum access')}</p>
            </div>
          </div>
        </div>

        {/* Org fields (non-individual) */}
        <AnimatePresence>
          {showOrgFields && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              style={{ background: CA, borderRadius: 24, padding: 32, border: `1px solid ${BR}`, boxShadow: '0 4px 24px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 20, overflow: 'hidden' }}>
              <h2 style={{ fontFamily: SE, color: G, margin: 0, fontSize: 20 }}>{t('sponsor.corporate.orgTitle', 'Organisation Details')}</h2>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('sponsor.corporate.orgNameLabel', 'Organisation Name')}</label>
                <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)}
                  placeholder={t('sponsor.corporate.orgNamePlaceholder', 'Your company or charity name')}
                  style={{ width: '100%', padding: '14px 18px', borderRadius: 14, border: `1px solid ${BR}`, background: 'rgba(0,0,0,0.2)', color: TX, fontSize: 14, outline: 'none' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: MU, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{t('sponsor.corporate.orgLogoLabel', 'Logo URL (optional)')}</label>
                <input type="url" value={orgLogo} onChange={e => setOrgLogo(e.target.value)}
                  placeholder={t('sponsor.corporate.orgLogoPlaceholder', 'https://yoursite.com/logo.png')}
                  style={{ width: '100%', padding: '14px 18px', borderRadius: 14, border: `1px solid ${BR}`, background: 'rgba(0,0,0,0.2)', color: TX, fontSize: 14, outline: 'none' }} />
                <p style={{ fontSize: 11, color: MU, margin: '8px 0 0', fontWeight: 500 }}>{t('sponsor.corporate.orgLogoHint', 'Used for branded impact photos if you opt in below.')}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Social / branding opt-in */}
        <div style={{ background: CA, borderRadius: 24, padding: 32, border: `1px solid ${BR}`, boxShadow: '0 4px 24px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            <h2 style={{ fontFamily: SE, color: G, margin: '0 0 8px', fontSize: 20 }}>{t('sponsor.corporate.recognitionTitle', 'Recognition (Optional)')}</h2>
            <p style={{ fontSize: 13, color: MU, margin: 0, lineHeight: 1.6 }}>
              {t('sponsor.corporate.recognitionSubtitle', 'We believe generosity deserves to be celebrated, tastefully. All photos are dignified and produced with care.')}
            </p>
          </div>

          {/* Social tag opt-in */}
          <div>
            <button
              onClick={() => setAllowTag(!allowTag)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: 20, borderRadius: 18, border: `2px solid ${allowTag ? G : BR}`,
                textAlign: 'left', transition: 'all 0.2s', background: allowTag ? 'rgba(212,168,67,0.05)' : 'transparent', cursor: 'pointer'
              }}
            >
              <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${allowTag ? G : MU}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: allowTag ? G : 'transparent' }}>
                {allowTag && <Check size={12} style={{ color: M }} strokeWidth={4} />}
              </div>
              <div>
                <p style={{ fontWeight: 800, fontSize: 13, margin: 0, color: allowTag ? G : TX }}>
                  {t('sponsor.corporate.tagLabel', 'Tag me on social media when we share impact stories')}
                </p>
                <p style={{ fontSize: 11, color: MU, margin: '2px 0 0', fontWeight: 500 }}>{t('sponsor.corporate.tagHint', 'Sage may share photos of children receiving education with a thank-you tag.')}</p>
              </div>
            </button>

            <AnimatePresence>
              {allowTag && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok', 'youtube'] as SocialPlatform[]).map(p => (
                      <button key={p} onClick={() => setSocialPlatform(p)}
                        style={{
                          flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${socialPlatform === p ? G : BR}`,
                          fontSize: 10, fontWeight: 800, transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          background: socialPlatform === p ? 'rgba(212,168,67,0.1)' : 'transparent', color: socialPlatform === p ? G : MU, cursor: 'pointer'
                        }}>
                        {SOCIAL_ICONS[p]} <span style={{ display: 'none' }} className="sm:inline capitalize">{p}</span>
                      </button>
                    ))}
                  </div>
                  <input type="text" value={socialHandle} onChange={e => setSocialHandle(e.target.value)}
                    placeholder={`@your${socialPlatform}handle`}
                    style={{ width: '100%', padding: '14px 18px', borderRadius: 14, border: `1px solid ${BR}`, background: 'rgba(0,0,0,0.2)', color: TX, fontSize: 14, outline: 'none' }} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Logo use opt-in */}
          {showOrgFields && (
            <button
              onClick={() => setAllowLogo(!allowLogo)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: 20, borderRadius: 18, border: `2.5px solid ${allowLogo ? G : BR}`,
                textAlign: 'left', transition: 'all 0.2s', background: allowLogo ? 'rgba(212,168,67,0.05)' : 'transparent', cursor: 'pointer'
              }}
            >
              <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${allowLogo ? G : MU}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: allowLogo ? G : 'transparent' }}>
                {allowLogo && <Check size={12} style={{ color: M }} strokeWidth={4} />}
              </div>
              <div>
                <p style={{ fontWeight: 800, fontSize: 13, margin: 0, color: allowLogo ? G : TX }}>
                  {t('sponsor.corporate.logoLabel', 'Include our logo in branded impact photos')}
                </p>
                <p style={{ fontSize: 11, color: MU, margin: '2px 0 0', fontWeight: 500 }}>{t('sponsor.corporate.logoHint', 'Your organisation logo is placed tastefully in impact photography.')}</p>
              </div>
            </button>
          )}

          {/* Anonymous */}
          <button
            onClick={() => setAnonymous(!anonymous)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: 20, borderRadius: 18, border: `2.5px solid ${anonymous ? G : BR}`,
              textAlign: 'left', transition: 'all 0.2s', background: anonymous ? 'rgba(212,168,67,0.05)' : 'transparent', cursor: 'pointer'
            }}
          >
            <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${anonymous ? G : MU}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: anonymous ? G : 'transparent' }}>
              {anonymous && <Check size={12} style={{ color: M }} strokeWidth={4} />}
            </div>
            <div>
              <p style={{ fontWeight: 800, fontSize: 13, margin: 0, color: anonymous ? G : TX }}>{t('sponsor.corporate.anonymousLabel', 'Remain anonymous')}</p>
              <p style={{ fontSize: 11, color: MU, margin: '2px 0 0', fontWeight: 500 }}>{t('sponsor.corporate.anonymousHint', 'Your identity will never be shared publicly or with recipients.')}</p>
            </div>
          </button>
        </div>

        {/* Update preference */}
        <div style={{ background: CA, borderRadius: 24, padding: 32, border: `1px solid ${BR}`, boxShadow: '0 4px 24px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <h2 style={{ fontFamily: SE, color: G, margin: '0 0 8px', fontSize: 20 }}>{t('sponsor.corporate.updatesTitle', 'Would you like updates?')}</h2>
            <p style={{ fontSize: 13, color: MU, margin: 0, lineHeight: 1.6 }}>
              {t('sponsor.corporate.updatesSubtitle', "Choose how much visibility you'd like into the children you sponsor.")}
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {([
              { val: 'none', label: t('sponsor.corporate.updatePrefs.none.label', 'No updates'), desc: t('sponsor.corporate.updatePrefs.none.desc', 'I prefer to give quietly with no reporting.') },
              { val: 'progress', label: t('sponsor.corporate.updatePrefs.progress.label', 'Anonymised dashboard'), desc: t('sponsor.corporate.updatePrefs.progress.desc', 'View learning progress in your sponsor dashboard — no names shown.') },
              { val: 'full', label: t('sponsor.corporate.updatePrefs.full.label', 'Named + full progress'), desc: t('sponsor.corporate.updatePrefs.full.desc', 'See the names of sponsored children and their complete learning journey.') },
            ] as const).map(opt => (
              <button key={opt.val} type="button" onClick={() => { setUpdatePref(opt.val); setUpdateTouched(true) }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'flex-start', gap: 16, padding: 20, borderRadius: 18, border: `2.5px solid ${updatePref === opt.val ? G : BR}`,
                  textAlign: 'left', transition: 'all 0.2s', background: updatePref === opt.val ? 'rgba(212,168,67,0.05)' : 'transparent', cursor: 'pointer'
                }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${updatePref === opt.val ? G : MU}`, flexShrink: 0, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {updatePref === opt.val && <div style={{ width: 10, height: 10, borderRadius: '50%', background: G }} />}
                </div>
                <div>
                  <p style={{ fontWeight: 800, fontSize: 13, margin: 0, color: updatePref === opt.val ? G : TX }}>{opt.label}</p>
                  <p style={{ fontSize: 11, color: MU, margin: '4px 0 0', lineHeight: 1.5 }}>{opt.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Advanced: dedication message */}
        <div style={{ background: CA, borderRadius: 24, border: `1px solid ${BR}`, overflow: 'hidden' }}>
          <button onClick={() => setShowAdvanced(!showAdvanced)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 24, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <div>
              <p style={{ fontWeight: 800, color: TX, margin: 0 }}>{t('sponsor.corporate.advancedTitle', 'Dedication Message')}</p>
              <p style={{ fontSize: 11, color: MU, margin: '4px 0 0' }}>{t('sponsor.corporate.advancedSubtitle', 'In memory of someone, or in honour of a milestone')}</p>
            </div>
            {showAdvanced ? <ChevronUp size={18} style={{ color: MU }} /> : <ChevronDown size={18} style={{ color: MU }} />}
          </button>
          <AnimatePresence>
            {showAdvanced && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 24, overflow: 'hidden' }}>
                <textarea value={dedication} onChange={e => setDedication(e.target.value)} rows={3}
                  placeholder={t('sponsor.corporate.dedicationPlaceholder', 'e.g. In loving memory of Sarah — who believed every child deserves a chance.')}
                  style={{ width: '100%', padding: '14px 18px', borderRadius: 14, border: `1px solid ${BR}`, background: 'rgba(0,0,0,0.2)', color: TX, fontSize: 14, outline: 'none', resize: 'none' }} />
                <p style={{ fontSize: 11, color: MU, margin: '8px 0 0' }}>{t('sponsor.corporate.dedicationHint', 'This message is shown to the recipient (if named sponsorship) and on your sponsor profile.')}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Trust badges */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 8 : 16 }}>
          {[
            { icon: <Shield size={16} />, label: t('sponsor.corporate.trust.secure.label', 'Secure Checkout'), sub: t('sponsor.corporate.trust.secure.sub', 'Powered by Stripe') },
            { icon: <Star size={16} />, label: t('sponsor.corporate.trust.cancel.label', 'Cancel Anytime'), sub: t('sponsor.corporate.trust.cancel.sub', 'No lock-in') },
            { icon: <Sparkles size={16} />, label: t('sponsor.corporate.trust.instant.label', 'Instant Access'), sub: t('sponsor.corporate.trust.instant.sub', 'Child enrolled same day') },
          ].map(b => (
            <div key={b.label} style={{ background: CA, borderRadius: 20, padding: 16, textAlign: 'center', border: `1px solid ${BR}` }}>
              <div style={{ color: G, display: 'flex', justifyContent: 'center', marginBottom: 8 }}>{b.icon}</div>
              <p style={{ fontSize: 10, fontWeight: 800, color: TX, margin: 0 }}>{b.label}</p>
              <p style={{ fontSize: 9, color: MU, margin: '2px 0 0', fontWeight: 500 }}>{b.sub}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button onClick={handleCheckout} disabled={loading}
          style={{
            display: isMobile ? 'none' : 'flex',
            width: '100%', padding: '24px', borderRadius: 20, background: loading ? BR : G,
            color: M, fontWeight: 800, fontSize: 18, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: `0 10px 40px ${loading ? 'rgba(0,0,0,0)' : 'rgba(212,168,67,0.3)'}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', alignItems: 'center', justifyContent: 'center', gap: 12
          }}>
          {loading ? t('sponsor.corporate.redirecting', 'Redirecting to checkout…') : (
            <>
              <Heart size={20} className="fill-current" />
              {t('sponsor.corporate.checkoutBtn', {
                seats,
                countLabel: seats === 1 ? t('sponsor.corporate.seat', 'Child') : t('sponsor.corporate.seats', 'Children'),
                amount: `$${total}${interval === 'month' ? '/mo' : interval === 'six_months' ? '/6mo' : '/yr'}`
              })}
              <ArrowRight size={18} />
            </>
          )}
        </button>

                <p style={{ textAlign: 'center', fontSize: 11, color: MU, fontWeight: 500, marginTop: 12 }}>
          {t('sponsor.corporate.footerNote', 'Secure payment via Stripe · Cancel anytime · Seats remain active until end of billing period')}
        </p>

        {/* Sticky mobile CTA — the button follows the scroll */}
        {isMobile && (
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50, background: 'rgba(6,6,12,0.95)', backdropFilter: 'blur(12px)', borderTop: `1px solid ${BR}`, padding: '12px 16px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            <button onClick={handleCheckout} disabled={loading}
              style={{ width: '100%', padding: '16px', borderRadius: 16, background: loading ? BR : G, color: M, fontWeight: 800, fontSize: 16, border: 'none', cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? t('sponsor.corporate.redirecting', 'Redirecting to checkout…') : `💛 Sponsor ${seats} ${seats === 1 ? t('sponsor.corporate.seat', 'child') : t('sponsor.corporate.seats', 'children')} — $${total}${interval === 'month' ? '/mo' : interval === 'six_months' ? '/6mo' : '/yr'}`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
